const DISPLAY_HEIGHT = 110;
const DISPLAY_WIDTH = 440;
const DPR = typeof window !== "undefined" ? window.devicePixelRatio || 2 : 2;
const MIN_HEIGHT = DISPLAY_HEIGHT * DPR;
const MAX_WIDTH = DISPLAY_WIDTH * DPR;
const SHARP_THRESHOLD = 2.5;

type QualityResult = { ok: boolean; reason?: string };

const readAsDataUrl = (file: File): Promise<string> =>
  // eslint-disable-next-line compat/compat
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject();
    reader.readAsDataURL(file);
  });

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
  // eslint-disable-next-line compat/compat
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject();
    img.src = dataUrl;
  });

const checkDimensions = (w: number, h: number): QualityResult | null => {
  if (h < MIN_HEIGHT)
    return {
      ok: false,
      reason: `Image height is too small (${h}px). Please upload an image at least ${MIN_HEIGHT}px tall.`
    };

  const requiredWidth = Math.max(MIN_HEIGHT, Math.min(w, MAX_WIDTH));
  if (w < requiredWidth)
    return {
      ok: false,
      reason: `Image width is too small (${w}px). Please upload an image at least ${requiredWidth}px wide.`
    };

  return null;
};

const checkSharpness = (img: HTMLImageElement): QualityResult => {
  const { naturalWidth: w, naturalHeight: h } = img;
  const scale = Math.min(1, 400 / Math.max(w, h));

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const cw = canvas.width;
  const ch = canvas.height;
  const gray = (i: number) =>
    0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];

  let sum = 0;
  let count = 0;
  for (let y = 1; y < ch - 1; y++)
    for (let x = 1; x < cw - 1; x++) {
      const i = (y * cw + x) * 4;
      sum += Math.abs(
        -4 * gray(i) +
          gray(i - cw * 4) +
          gray(i + cw * 4) +
          gray(i - 4) +
          gray(i + 4)
      );
      count++;
    }

  return sum / count < SHARP_THRESHOLD
    ? {
        ok: false,
        reason:
          "This image appears blurry or pixelated. Please upload a higher resolution version."
      }
    : { ok: true };
};

export const checkImageQuality = async (file: File): Promise<QualityResult> => {
  if (file.type === "image/svg+xml") return { ok: true };

  let img: HTMLImageElement;
  try {
    const dataUrl = await readAsDataUrl(file);
    img = await loadImage(dataUrl);
  } catch {
    return {
      ok: false,
      reason: "Unable to read this image file. Please try a different file."
    };
  }

  const dimensionResult = checkDimensions(img.naturalWidth, img.naturalHeight);
  if (dimensionResult) return dimensionResult;

  return checkSharpness(img);
};
